import React, { useState, useEffect } from 'react';
import { fetchLiveEvents } from '../utils/api';

const LiveStream = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Function to fetch live events from API
    async function getLiveEvents() {
      try {
        const data = await fetchLiveEvents();
        
        // Keep only the latest 20 events
        setEvents(prevEvents => {
          const combinedEvents = [...data, ...prevEvents];
          // Remove duplicates based on timestamp and type
          const uniqueEvents = combinedEvents.filter((event, index, self) => 
            index === self.findIndex(e => 
              e.timestamp === event.timestamp && e.type === event.type && 
              (e.pubkey === event.pubkey || (!e.pubkey && !event.pubkey))
            )
          );
          return uniqueEvents.slice(0, 20);
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching live events:', err);
        setError('Failed to fetch live events. Please try again later.');
        setLoading(false);
      }
    }

    getLiveEvents();
    
    // Set up polling interval (every 10 seconds)
    const intervalId = setInterval(getLiveEvents, 10000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Format timestamp to readable time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  // Format zap amount to readable format
  const formatZapAmount = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    } else {
      return `${amount}`;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse p-4 bg-dark-800 rounded-sm">
        <div className="h-4 bg-dark-700 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-dark-700 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-dark-700 rounded w-5/6 mb-2"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-danger-500 p-4 font-mono bg-dark-800 rounded-sm">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-dark-800 rounded-sm overflow-hidden scanlines">
      {events.length === 0 ? (
        <p className="text-primary-400 italic font-mono p-4">No recent activity to display.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-primary-700 scrollbar-track-dark-900">
          {events.map((event, index) => (
            <div 
              key={index} 
              className={`p-2 border-l-2 mb-1 ${
                event.type === 'note' 
                  ? 'border-primary-500 bg-dark-900' 
                  : 'border-accent-500 bg-dark-900'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  <span className={`font-pixel text-xs ${event.type === 'note' ? 'text-primary-400' : 'text-accent-400'}`}>
                    {event.type === 'note' ? 'NOTE' : 'ZAP'}
                  </span>
                  {event.type === 'zap' && (
                    <span className="ml-2 text-warning-400 font-mono text-xs">
                      {formatZapAmount(event.amount)} SATS
                    </span>
                  )}
                </div>
                <span className="text-xs text-primary-300 font-mono">
                  {formatTime(event.timestamp)}
                </span>
              </div>
              
              {event.content && (
                <p className="text-xs mt-1 text-primary-200 font-mono line-clamp-1">
                  {event.content}
                </p>
              )}
              
              {event.pubkey && (
                <p className="text-xs mt-1 text-primary-400 font-mono truncate">
                  npub: {event.pubkey.substring(0, 6)}...{event.pubkey.substring(event.pubkey.length - 6)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveStream; 